import { Component, inject, OnInit, Type } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PortalkonfigurationDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/portalkonfiguration-display-model';
import { LocalizedTextDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/localized-text-display-model';
import { FoerderprogrammDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/foerderprogramm-display-model';
import { I18nService } from '@/core/services/i18nService';
import { AusschreibungDisplayModel, GlobalEnumerationsAceSortierungausschreibungskacheln } from '@/api/model-index';
import { Constants } from '@/constants';
import { ComponentService } from '@/core/services/component.service';
import { AusschreibungCardComponent } from '../ausschreibung-card/ausschreibung-card.component';

@Component({
    selector: 'app-ausschreibung-overview',
    templateUrl: './ausschreibung-overview.component.html',
    styleUrls: ['./ausschreibung-overview.component.scss'],
    standalone: false
})
export class AusschreibungOverviewComponent implements OnInit {
  public portalkonfiguration : PortalkonfigurationDisplayModel;
  public foerderprogramme : FoerderprogrammDisplayModel[];
  public config = Constants.AUSSCHREIBUNG_OVERVIEW;
  
  protected componentService = inject(ComponentService);

  // Suche und Filter
  public searchTerm: string = '';
  public selectedFoerderprogrammId: string | null = null;

  // Cached values to prevent unnecessary re-rendering
  private _cachedFoerderprogrammFilters: Array<{ id: string | null, title: string }> | null = null;
  private _cachedSortedAusschreibungen: AusschreibungDisplayModel[] | null = null;
  private _cachedFilteredFoerderprogramme: FoerderprogrammDisplayModel[] | null = null;
  private _cachedFilteredAusschreibungen: AusschreibungDisplayModel[] | null = null;
  private _lastSearchTerm: string = '';
  private _lastSelectedFoerderprogrammId: string | null = null;

  constructor(protected route: ActivatedRoute,
    protected languageservice: I18nService) {
  }

  ngOnInit(): void {
    this.portalkonfiguration = this.route.snapshot.data.portalkonfiguration;
    this.foerderprogramme = this.route.snapshot.data.foerderprogramme;
    
    // Filter zurücksetzen wenn nach Ausschreibungsname sortiert wird
    if (this.sortByAusschreibungsname) {
      this.selectedFoerderprogrammId = null;
    }
  }

  public get ausschreibungCardComponent(): Type<AusschreibungCardComponent> {
    return this.componentService.get(AusschreibungCardComponent);
  }

  public onSearchChange(): void {
    // Cache invalidieren wenn sich Suchbegriff ändert
    this._invalidateFilterCaches();
  }

  public onFoerderprogrammChange(foerderprogrammId: string | null): void {
    this.selectedFoerderprogrammId = foerderprogrammId;
    // Cache invalidieren wenn sich Förderprogramm-Filter ändert
    this._invalidateFilterCaches();
  }

  private _invalidateFilterCaches(): void {
    this._cachedFilteredFoerderprogramme = null;
    this._cachedFilteredAusschreibungen = null;
  }

  public get showFoerderprogrammFilter(): boolean {
    return this.sortByFoerderprogramme && this.foerderprogramme && this.foerderprogramme.length > 1;
  }

  public get foerderprogrammFilters(): Array<{ id: string | null, title: string }> {
    // Return cached value if available
    if (this._cachedFoerderprogrammFilters !== null) {
      return this._cachedFoerderprogrammFilters;
    }

    if (!this.foerderprogramme) {
      return [];
    }
    
    const filters: Array<{ id: string | null, title: string }> = [
      { id: null, title: this.languageservice.instant('ausschreibung.filter.all') }
    ];
    this.foerderprogramme.forEach(fp => {
      if (fp.id && fp.title) {
        filters.push({ id: fp.id, title: fp.title });
      }
    });
    
    // Cache the result
    this._cachedFoerderprogrammFilters = filters;
    return filters;
  }

  public get willkommenstext(): string {
    let willkommenstext: LocalizedTextDisplayModel | undefined = this.languageservice.getCurrentLocalizedTextDisplayModel(this.portalkonfiguration.willkommenstexte);

    return willkommenstext?.text ?? "";
  }

  public get infotextUebersicht(): string {
    let infotextUebersicht: LocalizedTextDisplayModel | undefined = this.languageservice.getCurrentLocalizedTextDisplayModel(this.portalkonfiguration.infotexteUebersicht);

    return infotextUebersicht?.text ?? "";
  }

  public isNoEntryMessageVisible(index: number): boolean {
    if (! this.foerderprogramme[index])
      return true;
    else if (! this.foerderprogramme[index].ausschreibungen?.length)
      return true;

    return false;
  }



  private matchesSearch(ausschreibung: AusschreibungDisplayModel): boolean {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      return true;
    }

    const searchLower = this.searchTerm.toLowerCase();
    const name = this.languageservice.getCurrentLocalizedTextDisplayModel(ausschreibung.name)?.text?.toLowerCase() || '';
    const description = this.languageservice.getCurrentLocalizedTextDisplayModel(ausschreibung.goalDescription)?.text?.toLowerCase() || '';
    
    return name.includes(searchLower) || description.includes(searchLower);
  }

  private matchesFoerderprogramm(foerderprogrammId: string | null): boolean {
    if (this.selectedFoerderprogrammId === null) {
      return true; // "Alle" ausgewählt
    }
    
    return foerderprogrammId === this.selectedFoerderprogrammId;
  }

  public get filteredFoerderprogramme(): FoerderprogrammDisplayModel[] {
    // Check if cache is still valid
    if (this._cachedFilteredFoerderprogramme !== null && 
        this._lastSearchTerm === this.searchTerm && 
        this._lastSelectedFoerderprogrammId === this.selectedFoerderprogrammId) {
      return this._cachedFilteredFoerderprogramme;
    }

    if (!this.foerderprogramme) {
      return [];
    }

    // Update cache keys
    this._lastSearchTerm = this.searchTerm;
    this._lastSelectedFoerderprogrammId = this.selectedFoerderprogrammId;

    // Zuerst nach Förderprogramm filtern
    let programmeToShow = this.foerderprogramme;
    if (this.selectedFoerderprogrammId !== null) {
      programmeToShow = this.foerderprogramme.filter(fp => this.matchesFoerderprogramm(fp.id ?? null));
    }

    // Dann Ausschreibungen innerhalb der Programme nach Suchbegriff filtern
    const result = programmeToShow.map(foerderprogramm => {
      const filtered = Object.assign(Object.create(Object.getPrototypeOf(foerderprogramm)), foerderprogramm);
      if (foerderprogramm.ausschreibungen) {
        filtered.ausschreibungen = foerderprogramm.ausschreibungen.filter(ausschreibung => this.matchesSearch(ausschreibung));
      }
      return filtered;
    }).filter(foerderprogramm => foerderprogramm.ausschreibungen && foerderprogramm.ausschreibungen.length > 0);

    // Cache the result
    this._cachedFilteredFoerderprogramme = result;
    return result;
  }

  public get filteredAusschreibungen(): AusschreibungDisplayModel[] {
    // Check if cache is still valid
    if (this._cachedFilteredAusschreibungen !== null && 
        this._lastSearchTerm === this.searchTerm && 
        this._lastSelectedFoerderprogrammId === this.selectedFoerderprogrammId) {
      return this._cachedFilteredAusschreibungen;
    }

    let ausschreibungen = this.sortedAusschreibungen;
    
    // Nach Suchbegriff filtern
    ausschreibungen = ausschreibungen.filter(ausschreibung => this.matchesSearch(ausschreibung));
    
    // Bei Sortierung nach Ausschreibungsname KEIN Förderprogramm-Filter
    // (Immer alle Förderprogramme zeigen)
    // Nur wenn nach Förderprogramm sortiert wird, Filter anwenden
    if (this.sortByFoerderprogramme && this.selectedFoerderprogrammId !== null) {
      ausschreibungen = ausschreibungen.filter(ausschreibung => {
        // Finde das Förderprogramm, das diese Ausschreibung enthält
        const foerderprogramm = this.foerderprogramme.find(fp => 
          fp.ausschreibungen?.some(a => a.id === ausschreibung.id)
        );
        return foerderprogramm?.id === this.selectedFoerderprogrammId;
      });
    }
    
    // Cache the result
    this._cachedFilteredAusschreibungen = ausschreibungen;
    return ausschreibungen;
  }

  public get sortedAusschreibungen(): AusschreibungDisplayModel[] {
    // Return cached value if available
    if (this._cachedSortedAusschreibungen !== null) {
      return this._cachedSortedAusschreibungen;
    }

    let sortedAusschreibungen: AusschreibungDisplayModel[] = [];

    this.foerderprogramme.forEach(foerderprogramm => {
      if (foerderprogramm.ausschreibungen)
        sortedAusschreibungen.push(...foerderprogramm.ausschreibungen);
    });

    sortedAusschreibungen.sort((a, b) => {
      
      const nameA = this.languageservice.getCurrentLocalizedTextDisplayModel(a.name)?.text?.toLowerCase() || '';
      const nameB = this.languageservice.getCurrentLocalizedTextDisplayModel(b.name)?.text?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });

    // Cache the result
    this._cachedSortedAusschreibungen = sortedAusschreibungen;
    return sortedAusschreibungen;
  }

  public get isEmpty(): boolean {
    if (this.sortByFoerderprogramme) {
      return this.foerderprogramme.length === 0;
    } else {
      return this.sortedAusschreibungen.length === 0;
    }
  }

  public get isEmptySearchResult(): boolean {
    if (this.sortByFoerderprogramme) {
      return this.filteredFoerderprogramme.length === 0;
    } else {
      return this.filteredAusschreibungen.length === 0;
    }
  }

  public get sortierungAusschreibungskacheln(): GlobalEnumerationsAceSortierungausschreibungskacheln {
    if (! this.portalkonfiguration.sortierungAusschreibungskacheln)
      return GlobalEnumerationsAceSortierungausschreibungskacheln.Forderprogramm;

    return this.portalkonfiguration.sortierungAusschreibungskacheln;
  }

  public get sortByFoerderprogramme(): boolean {
    return this.sortierungAusschreibungskacheln == GlobalEnumerationsAceSortierungausschreibungskacheln.Forderprogramm;
  }

  public get sortByAusschreibungsname(): boolean {
    return this.sortierungAusschreibungskacheln == GlobalEnumerationsAceSortierungausschreibungskacheln.Ausschreibungsname;
  }

  public getFoerderprogrammNameForAusschreibung(ausschreibung: AusschreibungDisplayModel): string | undefined {
    if (!this.foerderprogramme) {
      return undefined;
    }

    const foerderprogramm = this.foerderprogramme.find(fp => 
      fp.ausschreibungen?.some(a => a.id === ausschreibung.id)
    );
    
    return foerderprogramm?.title ?? undefined;
  }
}
